import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Heart,
  Users,
  Shield,
  CheckCircle2,
  ArrowRight,
  Globe,
  Stethoscope,
  Code,
  Building,
  Crown,
  Quote,
  Lock,
  MessageCircle,
  Sparkles,
  ChevronRight,
} from "lucide-react";
import Navbar from "./Navbar";
import Footer from "../Components/Footer";
import coupleImage from "../Assets/Couple 1.png";
import coupleImage2 from "../Assets/couple2.png";
import coupleImage3 from "../Assets/couple3.png";
import coupleImage4 from "../Assets/couple4.png";
import coupleImage5 from "../Assets/couple5.png";
import coupleImage6 from "../Assets/couple6.png";
import coupleImage7 from "../Assets/couple7.png";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};
const stagger = { show: { transition: { staggerChildren: 0.08 } } };

const Home = () => {
  return (
    <div className="min-h-screen bg-white text-ink-800">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-mesh" />
        <div className="absolute -left-24 top-10 -z-10 h-72 w-72 rounded-full bg-brand-200/30 blur-3xl" />
        <div className="absolute -right-24 bottom-0 -z-10 h-72 w-72 rounded-full bg-amber-200/30 blur-3xl" />

        <div className="page-container py-12 sm:py-16 lg:py-24">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="chip">
                <Sparkles size={12} /> Trusted Goud Matrimony
              </span>
              <h1 className="mt-4 font-display text-4xl font-bold leading-[1.05] text-ink-900 sm:text-5xl lg:text-[3.6rem]">
                Find your forever, with{" "}
                <span className="bg-gradient-to-r from-brand-600 to-brand-800 bg-clip-text text-transparent">
                  grace.
                </span>
              </h1>
              <p className="mt-5 max-w-xl text-[15px] leading-7 text-ink-500 sm:text-[16px]">
                Connecting Goud families, professionals and NRIs with verified profiles, modern matchmaking and the warmth of tradition since 2020.
              </p>

              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/register" className="btn-primary px-6 py-3.5 text-[14.5px]">
                  Create your profile <ChevronRight size={16} />
                </Link>
                <Link to="/login" className="btn-ghost px-6 py-3.5 text-[14.5px]">
                  Browse matches
                </Link>
              </div>

            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-[32px] shadow-premium">
                <img src={coupleImage} alt="Happy Goud couple" className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-700/30 to-transparent" />
              </div>

              {/* Floating cards */}
              {[
                { Icon: Stethoscope, text: "Doctor match", pos: "top-4 left-4" },
                { Icon: Globe, text: "NRI alliance", pos: "top-1/2 right-3 -translate-y-1/2" },
                { Icon: CheckCircle2, text: "Verified profile", pos: "bottom-4 left-4" },
                { Icon: Crown, text: "Premium match", pos: "bottom-1/2 right-3 translate-y-1/2" },
              ].map(({ Icon, text, pos }, i) => (
                <motion.div
                  key={text}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + i * 0.12, duration: 0.5 }}
                  className={`absolute ${pos} flex items-center gap-2 rounded-2xl border border-white/60 bg-white/90 px-3 py-2 text-[12px] font-semibold text-ink-700 shadow-elevated backdrop-blur`}
                >
                  <Icon size={14} className="text-brand-600" />
                  {text}
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="page-container py-12 sm:py-16">
        <div className="text-center">
          <p className="section-subtle">Why Karanki</p>
          <h2 className="mt-1 font-display text-2xl font-bold text-ink-800 sm:text-3xl lg:text-4xl">
            Built for the Goud community
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-[14.5px] leading-7 text-ink-500">
            Tradition meets technology. The trust of family with the convenience of modern matchmaking.
          </p>
        </div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={stagger}
          className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
        >
          {[
            { Icon: Users, title: "Goud community focused", desc: "Exclusively connecting Goud families with shared values and traditions." },
            { Icon: Shield, title: "Verified profiles", desc: "Every profile is thoroughly verified for authenticity and genuineness." },
            { Icon: Globe, title: "NRI matchmaking", desc: "Specialised matchmaking for NRI Gouds seeking compatible partners." },
            { Icon: Stethoscope, title: "Doctor & professional matches", desc: "Premium matchmaking for doctors, engineers, and business professionals." },
            { Icon: Lock, title: "Privacy protected", desc: "Your personal information is secure with advanced privacy measures." },
            { Icon: Heart, title: "Personalised suggestions", desc: "AI-powered recommendations based on your preferences and compatibility." },
          ].map(({ Icon, title, desc }) => (
            <motion.div
              key={title}
              variants={fadeUp}
              whileHover={{ y: -4 }}
              className="card p-6 transition hover:shadow-elevated"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                <Icon size={20} />
              </div>
              <h3 className="mt-4 font-display text-[17px] font-bold text-ink-800">{title}</h3>
              <p className="mt-2 text-[13.5px] leading-7 text-ink-500">{desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* CATEGORIES */}
      <section className="page-container py-12 sm:py-16">
        <div className="text-center">
          <p className="section-subtle">Categories</p>
          <h2 className="mt-1 font-display text-2xl font-bold text-ink-800 sm:text-3xl">Find your perfect match</h2>
          <p className="mx-auto mt-3 max-w-2xl text-[14.5px] leading-7 text-ink-500">
            Explore curated categories designed for different lifestyles within the Goud community.
          </p>
        </div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={stagger}
          className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
        >
          {[
            { Icon: Stethoscope, title: "Doctor brides & grooms", desc: "Connect with medical professionals who understand your lifestyle." },
            { Icon: Code, title: "Software professionals", desc: "Find tech-savvy partners with similar career aspirations." },
            { Icon: Globe, title: "NRI matches", desc: "Global connections for Gouds living abroad." },
            { Icon: Users, title: "Traditional families", desc: "Matches that honour traditional Telugu values and customs." },
            { Icon: Building, title: "Business families", desc: "Entrepreneurial matches with business-minded families." },
            { Icon: Crown, title: "Premium verified", desc: "Elite, thoroughly verified profiles for discerning families." },
          ].map(({ Icon, title, desc }) => (
            <motion.div
              key={title}
              variants={fadeUp}
              whileHover={{ y: -4 }}
              className="card p-6 transition hover:shadow-elevated"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-gradient text-white shadow-soft">
                <Icon size={20} />
              </div>
              <h3 className="mt-4 font-display text-[17px] font-bold text-ink-800">{title}</h3>
              <p className="mt-2 text-[13.5px] leading-7 text-ink-500">{desc}</p>
              <Link to="/register" className="mt-4 inline-flex items-center gap-1 text-[13px] font-semibold text-brand-600">
                Explore <ArrowRight size={14} />
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* SUCCESS STORIES */}
      <section className="page-container py-12 sm:py-16">
        <div className="text-center">
          <p className="section-subtle">Success stories</p>
          <h2 className="mt-1 font-display text-2xl font-bold text-ink-800 sm:text-3xl">Real couples, real love</h2>
        </div>

        <div className="relative mt-8 overflow-hidden">
          <motion.div
            animate={{ x: [0, -344 * 6] }}
            transition={{ duration: 38, repeat: Infinity, ease: "linear", repeatDelay: 1 }}
            className="flex gap-4"
          >
            {[
              { name: "Ravi & Priya", year: "2023", story: "Karanki helped us find the perfect match within our community while respecting family values.", image: coupleImage2 },
              { name: "Suresh & Lakshmi", year: "2022", story: "As NRIs we were worried about finding compatible partners. Karanki made it seamless.", image: coupleImage3 },
              { name: "Dr. Kumar & Dr. Anjali", year: "2023", story: "Both doctors, we needed someone who understood our careers. We found our soulmate here.", image: coupleImage4 },
              { name: "Venkat & Meera", year: "2023", story: "After years of searching we found each other through Karanki — they truly understand traditions.", image: coupleImage5 },
              { name: "Arjun & Kavita", year: "2022", story: "As software professionals abroad, Karanki kept our cultural roots intact.", image: coupleImage6 },
              { name: "Rajesh & Sunita", year: "2023", story: "Verified profiles gave us the confidence to take the next step.", image: coupleImage7 },
            ].concat([
              { name: "Ravi & Priya", year: "2023", story: "Karanki helped us find the perfect match within our community while respecting family values.", image: coupleImage2 },
              { name: "Suresh & Lakshmi", year: "2022", story: "As NRIs we were worried about finding compatible partners. Karanki made it seamless.", image: coupleImage3 },
              { name: "Dr. Kumar & Dr. Anjali", year: "2023", story: "Both doctors, we needed someone who understood our careers. We found our soulmate here.", image: coupleImage4 },
              { name: "Venkat & Meera", year: "2023", story: "After years of searching we found each other through Karanki — they truly understand traditions.", image: coupleImage5 },
              { name: "Arjun & Kavita", year: "2022", story: "As software professionals abroad, Karanki kept our cultural roots intact.", image: coupleImage6 },
              { name: "Rajesh & Sunita", year: "2023", story: "Verified profiles gave us the confidence to take the next step.", image: coupleImage7 },
            ]).map((s, i) => (
              <div key={i} className="card flex w-80 shrink-0 flex-col gap-4 p-6 sm:w-[22rem]">
                <Quote size={22} className="text-brand-600" />
                <p className="text-[13.5px] leading-7 text-ink-600">"{s.story}"</p>
                <div className="flex items-center gap-3">
                  <img src={s.image} alt={s.name} className="h-12 w-12 rounded-full object-cover ring-1 ring-sand-200" />
                  <div>
                    <p className="font-display text-[14px] font-bold text-ink-800">{s.name}</p>
                    <p className="text-[11.5px] text-ink-400">Married · {s.year}</p>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="page-container py-12 sm:py-16">
        <div className="text-center">
          <p className="section-subtle">How it works</p>
          <h2 className="mt-1 font-display text-2xl font-bold text-ink-800 sm:text-3xl">Simple, secure, effective</h2>
        </div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={stagger}
          className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
        >
          {[
            { Icon: Users, title: "Create profile", desc: "Register with OTP verification and create your detailed profile." },
            { Icon: Shield, title: "Verify details", desc: "Our team verifies your information for authenticity and trust." },
            { Icon: Heart, title: "Get smart matches", desc: "Receive personalised match suggestions based on compatibility." },
            { Icon: MessageCircle, title: "Connect", desc: "Connect with families and matches." },
          ].map(({ Icon, title, desc }, i) => (
            <motion.div key={title} variants={fadeUp} className="card relative p-6 text-center">
              <span className="absolute right-3 top-3 inline-flex h-7 w-7 items-center justify-center rounded-full bg-brand-50 text-[11px] font-bold text-brand-700">
                {i + 1}
              </span>
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-gradient text-white shadow-soft">
                <Icon size={22} />
              </div>
              <h3 className="mt-4 font-display text-[16px] font-bold text-ink-800">{title}</h3>
              <p className="mt-1.5 text-[13px] leading-6 text-ink-500">{desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* CTA */}
      <section className="page-container py-12 sm:py-16">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative overflow-hidden rounded-[28px] bg-brand-gradient p-8 text-center text-white shadow-premium sm:p-14"
        >
          <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-16 -left-16 h-56 w-56 rounded-full bg-amber-200/15 blur-3xl" />
          <div className="relative">
            <h2 className="font-display text-2xl font-bold sm:text-4xl">Begin your journey today</h2>
            <p className="mx-auto mt-3 max-w-2xl text-[14.5px] leading-7 text-white/85 sm:text-[16px]">
              Join thousands of Goud families who found their perfect match through Karanki Matrimony.
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link to="/register" className="inline-flex items-center gap-2 rounded-2xl bg-white px-6 py-3 text-[14.5px] font-semibold text-brand-700 shadow-soft hover:bg-amber-50">
                Register free <ChevronRight size={16} />
              </Link>
              <Link to="/login" className="inline-flex items-center gap-2 rounded-2xl border border-white/30 bg-white/10 px-6 py-3 text-[14.5px] font-semibold text-white backdrop-blur hover:bg-white/20">
                Explore matches
              </Link>
            </div>
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
